import * as React from 'react';
import {StyleSheet, View, TouchableOpacity} from 'react-native';
import Styles from '../../Styles';
import AVFormattedCurrency from '../AVFormattedCurrency';
import AVText from '../AVText';
import type {CartItem as CartItemType} from 'src/types/TransactionDetail';
import SwipeableRow from '../DeleteSwipeableRow';
import CartTypes from 'src/constants/cart/CartTypes';
import CartStore from 'src/stores/CartStore';
import Localized from 'src/constants/AppStrings';

type CartItemProps = {
  trashClick?: (lineNumbers: Array<number>) => void;
  item: CartItemType;
  editClicked?: (item: CartItemType) => void;
  canEdit: boolean;
  canDelete: boolean;
  cartType: CartTypes;
};

class CartItem extends React.Component<CartItemProps> {
  swipeableRow: SwipeableRow;

  constructor(props: CartItemProps) {
    super(props);
    this.trashClick = this.trashClick.bind(this);
    this.updateRef = this.updateRef.bind(this);
    this.itemClicked = this.itemClicked.bind(this);
  }

  itemClicked() {
    if (this.props.editClicked) {
      this.props.editClicked(this.props.item);
    }
  }

  trashClick() {
    if (
      this.props.trashClick &&
      typeof this.props.item.LineNumber !== 'undefined'
    ) {
      this.props.trashClick([this.props.item.LineNumber]);
    }

    if (this.swipeableRow) {
      this.swipeableRow.close();
    }
  }

  updateRef(ref: SwipeableRow | null) {
    if (ref) {
      this.swipeableRow = ref;
    }
  }

  getCoffeeModifiers() {
    const coffeeModifiers = [];
    coffeeModifiers.push(
      <View style={[styles.rowContainer]}>
        <AVText
          numberOfLines={1}
          style={[styles.description, styles.modifierText]}
        >
          {CartStore.coffeeSelection.sizeName}
        </AVText>
      </View>,
    );

    if (CartStore.coffeeSelection.leaveRoom) {
      coffeeModifiers.push(
        <View style={[styles.rowContainer]}>
          <AVText
            numberOfLines={1}
            style={[styles.description, styles.modifierText]}
          >
            {Localized.Labels.leave_room}
          </AVText>
        </View>,
      );
    }

    if (CartStore.coffeeSelection.bold) {
      coffeeModifiers.push(
        <View style={[styles.rowContainer]}>
          <AVText
            numberOfLines={1}
            style={[styles.description, styles.modifierText]}
          >
            {Localized.Labels.bold}
          </AVText>
        </View>,
      );
    }

    return coffeeModifiers;
  }

  render() {
    let discountRow = null;

    if (
      this.props.item.Discount &&
      parseFloat(this.props.item.Discount) > 0 &&
      this.props.item.DiscountDisplayName
    ) {
      discountRow = (
        <View style={[styles.rowContainer]}>
          <AVText
            numberOfLines={1}
            style={[styles.description, styles.discountText]}
          >
            {this.props.item.DiscountDisplayName}
          </AVText>
          <AVText style={styles.discountAmountText}>
            -
            <AVFormattedCurrency
              accessibilityLabel="discount"
              value={parseFloat(this.props.item.Discount)}
            />
          </AVText>
        </View>
      );
    }

    let modifiers = null;

    if (this.props.item.Modifiers && this.props.item.Modifiers.length > 0) {
      this.props.item.Modifiers.sort((a, b) => b.Price - a.Price);

      modifiers = this.props.item.Modifiers.map((modifier, index) => {
        let priceText = null;

        if (modifier.Price) {
          //the type is listed as a number everywhere, but a string comes through
          const price = parseFloat(`${modifier.Price}`);

          if (price > 0) {
            priceText = (
              <AVText style={styles.discountText}>
                +
                <AVFormattedCurrency
                  accessibilityLabel="Modifier Price"
                  style={[styles.description, styles.modifierText]}
                  value={price}
                />
              </AVText>
            );
          }
        }

        return (
          <View key={index} style={[styles.rowContainer]}>
            <AVText
              numberOfLines={1}
              style={[styles.description, styles.modifierText]}
            >
              {modifier.Name}
            </AVText>
            {priceText}
          </View>
        );
      });
    }

    if (this.props.cartType === CartTypes.TouchlessCoffee) {
      modifiers = this.getCoffeeModifiers();
    }

    const row = (
      <TouchableOpacity
        style={styles.container}
        onPress={this.itemClicked}
        disabled={!this.props.canEdit}
      >
        <View style={styles.mainRow}>
          <View style={styles.rowContainer}>
            <View style={styles.mainSection}>
              <AVText style={styles.description}>
                {this.props.item.Description}
              </AVText>
              <AVText style={styles.status}>
                {this.props.item.SoldOut
                  ? `(${Localized.Labels.sold_out})`
                  : ''}
              </AVText>
            </View>
            <AVFormattedCurrency
              style={styles.amount}
              value={parseFloat(this.props.item.Price)}
              accessibilityLabel="itemPrice"
            />
          </View>
          {discountRow}
          {modifiers}
        </View>
      </TouchableOpacity>
    );

    if (!this.props.canDelete) {
      return row;
    } else {
      return (
        <SwipeableRow
          onDeletePressed={this.trashClick}
          type="remove"
          ref={this.updateRef}
        >
          {row}
        </SwipeableRow>
      );
    }
  }
}

const styles = StyleSheet.create({
  amount: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f1,
    marginRight: 0,
  },
  container: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'space-between',
    paddingVertical: Styles.Spacing.m2,
  },
  description: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f2,
    fontWeight: '300',
    marginRight: Styles.Spacing.m2,
  },
  discountAmountText: {
    color: Styles.positiveColor,
    fontSize: Styles.Fonts.f0,
  },
  discountText: {
    color: Styles.gray40,
    fontSize: Styles.Fonts.f0,
  },
  modifierText: {
    color: Styles.gray40,
    fontSize: Styles.Fonts.f1,
  },
  mainRow: {
    flex: 1,
    paddingLeft: Styles.Spacing.m1,
  },
  rowContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    paddingRight: Styles.Spacing.m2,
    justifyContent: 'space-between',
  },
  trashIcon: {
    marginHorizontal: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m1,
  },
  status: {
    color: Styles.dangerColor,
    textTransform: 'uppercase',
  },
  mainSection: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});
export default CartItem;
